import React, { useEffect, useRef, useState } from 'react';
import {
  Box, Flex, Text, Heading, Spinner, Drawer, DrawerOverlay, DrawerContent, DrawerCloseButton, DrawerHeader,
  DrawerBody, DrawerFooter, Link, Icon, Button,
} from '@chakra-ui/react';
import { FiFileText, FiExternalLink } from 'react-icons/fi';
import dayjs from 'dayjs';
import {
  getAiSitting, sittingAction, SITTING_STATUS, RUN_KIND, reasonLabel, formatScore, precheckReason, PAGE_ISSUE,
} from 'api/administrador/monitoring/aiGrading';
import { StatusPill } from './common';
import ConfirmActionModal from './ConfirmActionModal';

// Acciones sobre una entrega (quedan en Auditoría). La nota publicada no se revierte: se envía a revisión.
const ACTIONS = {
  hold: {
    label: 'Retener', confirm: 'Retener', success: 'Entrega retenida',
    description: 'Detiene la publicación de esta nota hasta que la liberes. El estudiante no ve nada.',
  },
  unhold: {
    label: 'Liberar', confirm: 'Liberar', success: 'Entrega liberada',
    description: 'Vuelve a quedar programada y se publica en su hora.', withReason: false,
  },
  regrade: {
    label: 'Volver a calificar', confirm: 'Volver a calificar', success: 'La entrega vuelve a la cola',
    description: 'Se descarta la nota actual y se califica de nuevo en la siguiente ejecución (cada 10 min).',
  },
  'to-teacher': {
    label: 'Pasar al docente', confirm: 'Pasar al docente', success: 'La entrega pasó al docente', primary: true,
    description: 'Aparece de inmediato en la bandeja del docente para calificación manual. La nota automática no se publica.',
  },
  'request-review': {
    label: 'Enviar a revisión del docente', confirm: 'Enviar a revisión', success: 'Enviada a revisión del docente', primary: true,
    description: 'La nota publicada se mantiene; el docente la ve en su bandeja para confirmarla o cambiarla.',
  },
};

const ACTIONS_BY_STATUS = {
  to_publish: ['hold', 'regrade', 'to-teacher'],
  held: ['unhold', 'regrade', 'to-teacher'],
  queued: ['to-teacher'],
  teacher: ['regrade'],
  published: ['request-review'],
  teacher_graded: [],
};

const contentDoc = (html) => `<!doctype html><html><head><meta charset="utf-8"><style>
body{margin:12px;font:14px/1.5 -apple-system,BlinkMacSystemFont,'Segoe UI',Helvetica,sans-serif;color:#2D3748}
img{max-width:100%;border-radius:6px}</style></head><body>${html}</body></html>`;

const when = (d) => (d ? dayjs(d).format('DD MMM · HH:mm') : '—');

function statusLine(s) {
  if (s.status === 'held') return `Retenida por ${s.hold?.by?.name || 'un administrador'} el ${when(s.hold?.at)}${s.hold?.reason ? ` · ${s.hold.reason}` : ''}`;
  if (s.status === 'published' && s.reviewRequested) return `Publicada el ${when(s.releasedAt)} · en revisión del docente`;
  if (s.status === 'to_publish') return s.publishAt ? `El estudiante la verá el ${when(s.publishAt)}` : 'Esperando su hora de publicación';
  if (s.status === 'published') return `Publicada el ${when(s.releasedAt)}`;
  if (s.status === 'teacher') return reasonLabel(s.reason) || 'Pasada al docente';
  if (s.status === 'teacher_graded') return 'El docente la calificó antes de que se publicara';
  return 'Esperando la siguiente ejecución de calificación';
}

function Timeline({ s }) {
  const steps = [
    { label: 'Enviada con revisión previa', meta: when(s.createdAt), done: true },
    ...(s.history || []).map((h) => ({
      label: `${RUN_KIND[h.kind]?.label || h.kind} · ${
        h.outcome === 'graded' ? `nota ${formatScore(h.score)}`
          : h.outcome === 'deferred' ? `al docente (${reasonLabel(h.reason) || 'sin motivo'})`
            : h.outcome === 'released' ? 'publicada'
              : h.outcome === 'retry' ? 'reintento'
                : h.outcome
      }`,
      meta: `${when(h.started_at)} · ejecución #${h.run_id}`,
      done: h.outcome !== 'error' && h.outcome !== 'retry',
      err: h.outcome === 'error',
    })),
  ];
  if (s.status === 'to_publish') steps.push({ label: 'Publicación programada', meta: when(s.publishAt), pending: true });
  return (
    <Box as="ol" listStyleType="none" m={0} p={0}>
      {steps.map((st, i) => (
        <Flex as="li" key={i} gap={3} pb={3}>
          <Box
            w="10px" h="10px" mt="5px" flexShrink={0} borderRadius="999px"
            bg={st.err ? '#EF4444' : st.pending ? '#3B82F6' : st.done ? '#10B981' : '#CBD5E0'}
          />
          <Box>
            <Text fontSize="sm" color="gray.800">{st.label}</Text>
            <Text fontSize="xs" color="gray.500">{st.meta}</Text>
          </Box>
        </Flex>
      ))}
    </Box>
  );
}

function checkResult(c) {
  if (c.forced) return { label: 'Insertada de todas formas', tone: 'orange' };
  if (c.ok === true) return { label: 'Revisado', tone: 'green' };
  if (c.ok === false) return { label: 'Difícil de leer', tone: 'orange' };
  return { label: 'Sin revisar', tone: 'gray' };
}

/** Cada revisión previa (archivo o imagen) que hizo el estudiante antes de enviar, en orden. */
function PrecheckChecks({ checks, override }) {
  const failed = checks.filter((c) => c.ok === false && !c.forced).length;
  return (
    <Box>
      <Text fontSize="sm" fontWeight="600" mb={1}>Revisión previa al enviar</Text>
      <Text fontSize="xs" color="gray.600" mb={3}>
        {override
          ? `El estudiante la envió de todas formas después de ${failed} ${failed === 1 ? 'revisión fallida' : 'revisiones fallidas'}. La entrega completa pasó al docente.`
          : failed
            ? `Tuvo ${failed} ${failed === 1 ? 'aviso' : 'avisos'} y lo corrigió antes de enviar.`
            : 'Todo pasó la revisión a la primera.'}
      </Text>
      <Flex direction="column" gap={2}>
        {checks.map((c) => {
          const r = checkResult(c);
          const issues = (c.issues || []).map((i) => `pág. ${i.page} ${(PAGE_ISSUE[i.issue] || i.issue || '').toLowerCase()}`).join(', ');
          return (
            <Box key={c.id} border="1px solid" borderColor={c.ok === false ? '#FDE68A' : 'gray.200'} bg={c.ok === false ? '#FFFBEB' : 'white'} borderRadius="10px" px={3} py={2}>
              <Flex justify="space-between" align="center" gap={2}>
                <Text fontSize="13px" fontWeight="600" isTruncated>
                  {c.kind === 'pdf' ? 'PDF' : 'Imagen'} · {c.fileName || 'archivo'}
                </Text>
                <StatusPill label={r.label} tone={r.tone} />
              </Flex>
              <Text fontSize="12px" color="gray.600" mt={1}>
                {[
                  c.attempt ? `Intento ${c.attempt}` : null,
                  c.pages ? `${c.pages} ${c.pages === 1 ? 'página' : 'páginas'}` : null,
                  c.ok === false ? precheckReason(c.reason) : null,
                  issues || null,
                  c.submitted ? 'va en la entrega' : null,
                  dayjs(c.at).format('HH:mm'),
                ].filter(Boolean).join(' · ')}
              </Text>
            </Box>
          );
        })}
      </Flex>
    </Box>
  );
}

export default function SittingDetailDrawer({ sittingId, onClose, onChanged }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [pending, setPending] = useState(null);
  const [reload, setReload] = useState(0);

  const shownId = useRef(null);

  useEffect(() => {
    if (!sittingId) return;
    setError(null);
    // Otra entrega: se limpia; la misma tras una acción: se refresca sin parpadeo.
    if (shownId.current !== sittingId) setData(null);
    shownId.current = sittingId;
    getAiSitting(sittingId).then(setData).catch(() => setError('No se pudo cargar la entrega.'));
  }, [sittingId, reload]);

  const available = data ? (ACTIONS_BY_STATUS[data.status] || []).filter((a) => !(a === 'request-review' && data.reviewRequested)) : [];
  const action = pending ? ACTIONS[pending] : null;

  const s = data;
  const status = s ? SITTING_STATUS[s.status] || SITTING_STATUS.queued : null;
  const shownScore = s ? (s.status === 'teacher_graded' ? s.score : s.aiScore) : null;
  const precheckPages = s?.precheck?.files?.flatMap((f) => f.pages || []) || [];
  const unreadable = precheckPages.filter((p) => p.issue && p.issue !== 'ok').length;

  return (
    <Drawer isOpen={!!sittingId} placement="right" onClose={onClose} size="md">
      <DrawerOverlay />
      <DrawerContent>
        <DrawerCloseButton top={5} />
        <DrawerHeader borderBottomWidth="1px" pr={12}>
          {!s && !error && <Spinner color="#574feb" size="sm" />}
          {error && <Text color="red.600" fontSize="sm">{error}</Text>}
          {s && (
            <Box>
              <Text fontSize="xs" color="gray.500" fontWeight="500">
                {[s.subject?.name, s.program?.name].filter(Boolean).join(' · ')}
              </Text>
              <Heading fontSize="20px" fontWeight="700" mt={1}>{s.activity?.title || `Actividad ${s.activity?.id}`}</Heading>
              <Text fontSize="sm" color="gray.700" fontWeight="400" mt={1}>
                {s.student?.name || 'Estudiante'} · enviada {when(s.createdAt)} · {s.kind === 'PDF' ? 'PDF' : 'texto'}
              </Text>
              <Flex gap={2} align="center" mt={3}>
                <StatusPill label={status.label} tone={status.tone} />
                <Text fontSize="13px" color="gray.600" fontWeight="400">{statusLine(s)}</Text>
              </Flex>
            </Box>
          )}
        </DrawerHeader>

        <DrawerBody py={5}>
          {s && (
            <Flex direction="column" gap={6}>
              {(shownScore !== null || s.aiFeedback) && (
                <Flex gap={4} align="stretch">
                  <Flex
                    w="104px" flexShrink={0} direction="column" align="center" justify="center" borderRadius="12px"
                    bg="#F5F3FF" border="1px solid" borderColor="#DDD6FE" py={3}
                  >
                    <Text fontSize="36px" fontWeight="800" color="#4C1D95" lineHeight="1">{formatScore(shownScore)}</Text>
                    <Text fontSize="xs" color="#5B21B6">de 10</Text>
                  </Flex>
                  <Box>
                    <Text fontSize="11px" fontWeight="700" letterSpacing=".06em" color="gray.500" textTransform="uppercase">
                      {s.status === 'teacher_graded' ? 'Mensaje del docente' : 'Retroalimentación (la verá el estudiante)'}
                    </Text>
                    <Text fontSize="sm" color="gray.700" mt={1} lineHeight="1.55">
                      {(s.status === 'teacher_graded' ? s.answer : s.aiFeedback) || '—'}
                    </Text>
                  </Box>
                </Flex>
              )}

              <Box>
                <Text fontSize="sm" fontWeight="600" mb={2}>Lo que envió el estudiante</Text>
                {s.files.length > 0 && (
                  <Flex direction="column" gap={2} mb={3}>
                    {s.files.map((f) => (
                      <Link key={f.url} href={f.url} isExternal display="flex" alignItems="center" gap={2} fontSize="sm" color="#574feb">
                        <Icon as={FiFileText} />{f.name}<Icon as={FiExternalLink} boxSize={3} />
                      </Link>
                    ))}
                  </Flex>
                )}
                {s.content ? (
                  // HTML escrito por el estudiante: se muestra en un iframe aislado (sin scripts ni acceso a la sesión).
                  <Box
                    as="iframe" title="Respuesta del estudiante" sandbox="" srcDoc={contentDoc(s.content)}
                    w="100%" h="240px" border="1px solid" borderColor="gray.200" borderRadius="10px" bg="white"
                  />
                ) : s.files.length === 0 && <Text fontSize="sm" color="gray.500">Sin contenido.</Text>}
                {s.precheck && !(s.checks || []).length && (
                  <Text fontSize="xs" color="gray.500" mt={2}>
                    Revisión previa al enviar: {s.precheck.ok === false ? 'con problemas'
                      : precheckPages.length ? `${precheckPages.length - unreadable} de ${precheckPages.length} páginas legibles` : 'sin problemas'}
                  </Text>
                )}
              </Box>

              {(s.checks || []).length > 0 && <PrecheckChecks checks={s.checks} override={!!s.precheck?.override} />}

              <Box>
                <Text fontSize="sm" fontWeight="600" mb={2}>Recorrido</Text>
                <Timeline s={s} />
              </Box>

              {s.aiResult && (
                <Box as="details" border="1px solid" borderColor="gray.200" borderRadius="10px" px={3} py={2} fontSize="13px" color="gray.600">
                  <Box as="summary" cursor="pointer" fontWeight="600" color="gray.700">Datos técnicos</Box>
                  <Text mt={2}>
                    {[s.aiResult.model && `Modelo ${s.aiResult.model}`, s.aiResult.images ? `${s.aiResult.images} imágenes` : null,
                      s.aiResult.tokens ? `${s.aiResult.tokens} tokens` : null, s.aiResult.ms ? `${(s.aiResult.ms / 1000).toFixed(1)} s` : null,
                      s.attempts ? `${s.attempts} intentos` : null].filter(Boolean).join(' · ') || '—'}
                  </Text>
                </Box>
              )}
            </Flex>
          )}
        </DrawerBody>

        {s && available.length > 0 && (
          <DrawerFooter borderTopWidth="1px" flexDirection="column" alignItems="stretch" gap={2}>
            <Flex gap={2}>
              {available.map((key) => (
                <Button
                  key={key}
                  flex={1}
                  borderRadius="10px"
                  onClick={() => setPending(key)}
                  variant={ACTIONS[key].primary ? 'solid' : 'outline'}
                  bg={ACTIONS[key].primary ? '#574feb' : 'white'}
                  color={ACTIONS[key].primary ? 'white' : 'gray.700'}
                  _hover={{ bg: ACTIONS[key].primary ? '#4338CA' : 'gray.50' }}
                >
                  {ACTIONS[key].label}
                </Button>
              ))}
            </Flex>
            <Text fontSize="12px" color="gray.500">Las acciones quedan registradas en Auditoría.</Text>
          </DrawerFooter>
        )}
      </DrawerContent>

      <ConfirmActionModal
        isOpen={!!pending}
        onClose={() => setPending(null)}
        title={action?.label}
        description={action?.description}
        confirmLabel={action?.confirm}
        withReason={action?.withReason !== false}
        successMessage={action?.success}
        onConfirm={(reason) => sittingAction(sittingId, pending, reason)}
        onDone={() => {
          setReload((n) => n + 1);
          if (onChanged) onChanged();
        }}
      />
    </Drawer>
  );
}
